import {
    FC, useMemo, useState, useEffect,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Card from '@ui/Card';
import CardContent from '@ui/CardContent';
import Typography from '@ui/Typography';
import Grid from '@ui/Grid';
import { lightGreyColor } from '@ui/styles/colors';
import { ShapeId } from '@core/constants/Shape';
import { IFieldShapeQuantities } from '@core/interfaces/IFields';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import styled from '@ui/styles/styled';
import formatDate from '../../../utils/formatDate';
import firebaseService from '../../../services/Firebase';
import {
    IFormFieldKey,
    IFormFieldContactKey,
    IFormFieldAdditionalDataKey,
} from '../../../interfaces/Form';
import { IFormFieldRecapProperties } from './types';
import {
    recapCardContentStyle,
    recapGridItemStyle,
    recapGridContainerStyle,
    recapGridContainerFormFlowStyle,
    recapCardStyle,
    titleStyle,
    recapGridItemFormFlowStyle,
} from './styles';
import FormFieldRecapItem from './FormFieldRecapItem';
import FormFieldRecapItemMaterial from './FormFieldRecapItemMaterial';
import FormFieldRecapItemShape from './FormFieldRecapItemShape';
import FormFieldRecapItemDimension from './FormFieldRecapItemDimension';
import FormFieldRecapItemOption from './FormFieldRecapItemOption';
import FormFieldRecapItemShapeQuantity from './FormFieldRecapItemShapeQuantity';
import FormFieldRecapItemRadioListOption from './FormFieldRecapItemRadioListOption';
import FormFieldRecapContainerWithTitle from './FormFieldRecapContainerWithTitle';
import FormFieldRecapItemContact from './FormFieldRecapItemContact';

const StyledTitle = styled(Typography)(titleStyle);

const getShapeFromShapeQuantity = (shapeQuantity:IFieldShapeQuantities<ShapeId>) => Object
    .entries(shapeQuantity)
    .map(([shapeId, quantityField]) => ({ id: shapeId as ShapeId, value: quantityField || 0 }));

const FormFieldRecap: FC<IFormFieldRecapProperties> = ({
    fields,
    shouldHideContact,
    shouldHideLookingFor,
    shouldHideSubTitle,
    shouldHideTitle,
    inverted,
    creationDate,
}) => {
    const intl = useIntl();

    const [uploadFileUrl, setUploadFileUrl] = useState<string | string[] | undefined>();

    const uploadFile = fields.additionalData?.uploadFile;
    const secondaryUploadFile = fields.uploadFiles;

    useEffect(() => {
        (async () => {
            if (typeof uploadFile === 'string') {
                setUploadFileUrl(await (await firebaseService.getInstance()).getSubmissionImageURL(uploadFile));
            }

            if (uploadFile && Array.isArray(uploadFile)) {
                let filesUrls = [];
                if (uploadFile) {
                    filesUrls = await Promise.all((uploadFile).map(async (fileUrl) => (await firebaseService.getInstance()).getSubmissionImageURL(fileUrl)));
                    setUploadFileUrl(filesUrls);
                }
            }
        })();
    }, [uploadFile, setUploadFileUrl]);

    useEffect(() => {
        (async () => {
            if (typeof secondaryUploadFile === 'string') {
                setUploadFileUrl(await (await firebaseService.getInstance()).getSubmissionImageURL(secondaryUploadFile));
            }

            if (secondaryUploadFile && Array.isArray(secondaryUploadFile)) {
                let filesUrls = [];
                if (secondaryUploadFile) {
                    filesUrls = await Promise.all((secondaryUploadFile).map(async (fileUrl) => (await firebaseService.getInstance()).getSubmissionImageURL(fileUrl)));
                    setUploadFileUrl(filesUrls);
                }
            }
        })();
    }, [secondaryUploadFile, setUploadFileUrl]);

    const portfolioData = useMemo(() => fields?.portfolioData, [fields]);
    const addOns = useMemo(() => fields?.addOns && getShapeFromShapeQuantity(fields.addOns), [fields]);
    const connectics = useMemo(() => fields?.connectics && getShapeFromShapeQuantity(fields.connectics), [fields]);
    const cutting = useMemo(() => fields?.cutting && getShapeFromShapeQuantity(fields.cutting), [fields]);
    const doors = useMemo(() => fields?.openings && getShapeFromShapeQuantity(fields.openings), [fields]);
    const kitchenFurniture = useMemo(() => fields?.kitchenFurniture && getShapeFromShapeQuantity(fields.kitchenFurniture), [fields]);
    const kitchenHomeAppliance = useMemo(() => fields?.kitchenHomeAppliance && getShapeFromShapeQuantity(fields.kitchenHomeAppliance), [fields]);
    const obstacles = useMemo(() => fields?.obstacles && getShapeFromShapeQuantity(fields.obstacles), [fields]);
    const pergolaOptions = useMemo(() => fields?.pergolaOptions && getShapeFromShapeQuantity(fields.pergolaOptions), [fields]);
    const quantity = useMemo(() => fields?.quantity && getShapeFromShapeQuantity(fields.quantity), [fields]);
    const shoesQuantity = useMemo(() => fields?.shoesQuantity && getShapeFromShapeQuantity(fields.shoesQuantity), [fields]);
    const trayDimension = useMemo(() => (fields?.trayDimension || fields?.trayShapeDimension?.dimension), [fields]);
    const trayShape = useMemo(() => fields?.trayShape || fields?.trayShapeDimension?.shape, [fields]);
    const vegetals = useMemo(() => fields?.vegetals && getShapeFromShapeQuantity(fields.vegetals), [fields]);
    const wineCellarNbBottle = useMemo(() => fields?.wineCellarNbBottle && getShapeFromShapeQuantity(fields.wineCellarNbBottle), [fields]);
    const wineCellarNbDoor = useMemo(() => fields?.wineCellarNbDoor && getShapeFromShapeQuantity(fields.wineCellarNbDoor), [fields]);
    const wineCellarOption = useMemo(() => fields?.wineCellarOption && getShapeFromShapeQuantity(fields.wineCellarOption), [fields]);
    const wineCellarSensDoor = useMemo(() => fields?.wineCellarSensDoor && getShapeFromShapeQuantity(fields.wineCellarSensDoor), [fields]);
    const wineCellarShelf = useMemo(() => fields?.wineCellarShelf && getShapeFromShapeQuantity(fields.wineCellarShelf), [fields]);

    return fields ? (
        <>
            {!shouldHideTitle && (
                <StyledTitle variant="h6" variantMapping={{ h6: 'h3' }} color="primary">
                    <FormattedMessage id="submissionSummary" />
                </StyledTitle>
            )}

            <br />

            {portfolioData && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id="descriptionRecap" />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="space-evenly"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {!shouldHideSubTitle && (
                            <Grid xs={12} md={5} item container spacing={1}>
                                <Grid xs={12} md={12} item style={recapGridItemStyle}>
                                    <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                        <CardContent style={recapCardContentStyle}>
                                            <FormFieldRecapItem
                                                label={intl.formatMessage({ id: 'portfolioData.title.label' })}
                                                value={portfolioData.title}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid xs={12} md={12} item style={recapGridItemStyle}>
                                    <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                        <CardContent style={recapCardContentStyle}>
                                            <FormFieldRecapItem
                                                label={intl.formatMessage({ id: 'portfolioData.place.label' })}
                                                value={portfolioData.place}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid xs={12} md={12} item style={recapGridItemStyle}>
                                    <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                        <CardContent style={recapCardContentStyle}>
                                            <FormFieldRecapItem
                                                label={intl.formatMessage({ id: 'portfolioData.price.label' })}
                                                value={`${portfolioData.price.toString()}€`}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {!shouldHideSubTitle && (
                            <Grid xs={12} md={7} container item style={recapGridItemStyle}>
                                <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                    <CardContent style={recapCardContentStyle}>
                                        <FormFieldRecapItem
                                            label={intl.formatMessage({ id: 'portfolioData.description.label' })}
                                            value={portfolioData.description}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {portfolioData.images && portfolioData.images.map((image) => (
                            <Grid xs={6} sm={4} md={!shouldHideSubTitle ? 3 : 6} item style={recapGridItemStyle} key={image.id}>
                                <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                    <CardContent style={recapCardContentStyle}>
                                        <img src={image.url} alt={image.id} style={{ height: !shouldHideSubTitle ? 100 : 200, objectFit: 'cover', width: '100%' }} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}

                        {shouldHideSubTitle && (
                            <Grid xs={12} md={12} item style={recapGridItemFormFlowStyle}>
                                <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                    <CardContent style={recapCardContentStyle}>
                                        <FormFieldRecapItem
                                            label={intl.formatMessage({ id: 'portfolioData.title.label' })}
                                            value={portfolioData.title}
                                        />
                                    </CardContent>
                                </Card>
                                <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                    <CardContent style={recapCardContentStyle}>
                                        <FormFieldRecapItem
                                            label={intl.formatMessage({ id: 'portfolioData.price.label' })}
                                            value={`${portfolioData.price.toString()}€`}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {(fields.projectInformation || fields?.kitchenProjectType) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id="forms.fieldsCategories.dwelling.title" />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {fields?.projectInformation?.property && (
                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: 'components.FormFieldProjectInformation.property.label' })}
                                value={intl.formatMessage({ id: `components.FormFieldProjectInformation.property.${fields?.projectInformation.property}.recap` })}
                                inverted={inverted}
                            />
                        )}

                        {fields?.projectInformation?.ownerTenant && (
                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: 'components.FormFieldProjectInformation.ownerTenant.label' })}
                                value={intl.formatMessage({ id: `components.FormFieldProjectInformation.ownerTenant.${fields?.projectInformation.ownerTenant}.label` })}
                                inverted={inverted}
                            />
                        )}

                        {fields?.budget && (
                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: `components.FormFieldRadioList.radioListOptions.budgetOption.${fields?.budget}.label` })}
                                value={intl.formatMessage({ id: `components.FormFieldRadioList.radioListOptions.budgetOption.${fields?.budget}.title` })}
                                inverted={inverted}
                            />
                        )}

                        {fields?.kitchenProjectType && (
                            <FormFieldRecapItemRadioListOption
                                fieldKey={IFormFieldKey.KitchenProjectType}
                                option={fields?.kitchenProjectType}
                                inverted={inverted}
                            />
                        )}
                    </Grid>
                </>
            )}

            {fields?.description && !shouldHideSubTitle && (
                <>
                    <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                        <FormattedMessage id="detail" />
                    </Typography>

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        <Grid xs={12} item style={recapGridItemStyle}>
                            <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                <CardContent style={recapCardContentStyle}>
                                    <FormFieldRecapItem
                                        label={intl.formatMessage({ id: 'fieldsChat.description.label' })}
                                        value={fields?.description}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {fields?.designProject && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id="forms.fieldsCategories.designProjects.title" />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {Object.entries(fields?.designProject).map(([designProjectCategoryId, designProjectCategory]) => (
                            // @ts-ignore
                            Object.entries(designProjectCategory)
                            // @ts-ignore
                                .filter(([_b, designProject]) => (designProject !== undefined ? designProject > 0 : false))
                            // @ts-ignore
                                .map(([designProjectId, designProject]) => (
                                    <Grid key={designProjectId} xs={4} item style={recapGridItemStyle}>
                                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                            <CardContent style={recapCardContentStyle}>
                                                <FormFieldRecapItem
                                                    label={`${intl.formatMessage({ id: `designProjectCategories.${designProjectCategoryId}.name` })} - ${intl.formatMessage({ id: `designProjects.${designProjectId}.name` })}`}
                                                    value={designProject !== undefined ? `${designProject}` : '-'}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))
                        ))}
                    </Grid>
                </>
            )}

            <FormFieldRecapContainerWithTitle
                title={intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.BedShapeDimension}.label` })}
                showTitle={shouldHideSubTitle}
            >

                {fields?.bedShapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.BedShapeDimension}
                        fieldShape={fields?.bedShapeDimension?.shape}
                        fieldShapeTumbnail={fields?.bedShapeDimension?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.bedShapeDimension?.dimension && Object
                    .entries(fields?.bedShapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.topBedShapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.TopBedShapeDimension}
                        fieldShape={fields?.topBedShapeDimension?.shape}
                        fieldShapeTumbnail={fields?.topBedShapeDimension?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.topBedShapeDimension?.dimension && Object
                    .entries(fields?.topBedShapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

            </FormFieldRecapContainerWithTitle>

            <FormFieldRecapContainerWithTitle
                title="forms.fieldsCategories.tray.title"
                showTitle={shouldHideSubTitle}
            >
                {fields?.trayMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.trayMaterial}
                        fieldKey={IFormFieldKey.TrayMaterial}
                        inverted={inverted}
                    />
                )}

                {trayShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.TrayShape}
                        fieldShape={trayShape}
                        fieldShapeTumbnail={trayShape}
                        inverted={inverted}
                    />
                )}

                {trayDimension && Object
                    .entries(trayDimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.extension?.shape && (
                    <>
                        <FormFieldRecapItemShape
                            fieldKey={IFormFieldKey.Extension}
                            fieldShape={fields?.extension?.shape}
                            fieldShapeTumbnail={fields?.extension?.shape}
                            inverted={inverted}
                        />

                        {fields?.extension?.dimension && Object
                            .entries(fields?.extension?.dimension)
                            .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                            .map(([dimensionKey, dimensionField]) => (
                                <FormFieldRecapItemDimension
                                    key={dimensionKey}
                                    dimensionKey={dimensionKey}
                                    dimensionField={dimensionField}
                                    inverted={inverted}
                                />
                            ))}
                    </>
                )}
            </FormFieldRecapContainerWithTitle>

            <FormFieldRecapContainerWithTitle
                title="forms.fieldsCategories.specifications.title"
                showTitle={shouldHideSubTitle}
            >

                {fields?.portalShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.PortalShape}
                        fieldShape={fields?.portalShape}
                        fieldShapeTumbnail={fields?.portalShape}
                        inverted={inverted}
                    />
                )}

                {fields?.portalShapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.PortalShapeDimension}
                        fieldShape={fields?.portalShapeDimension.shape}
                        fieldShapeTumbnail={fields?.portalShapeDimension.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.portalShapeDimension?.dimension && Object
                    .entries(fields?.portalShapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.portalOpening && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.PortalOpening}
                        fieldShape={fields?.portalOpening}
                        fieldShapeTumbnail={fields?.portalOpening}
                        inverted={inverted}
                    />
                )}

                {fields?.portalFilling && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.PortalFilling}
                        fieldShape={fields?.portalFilling}
                        fieldShapeTumbnail={fields?.portalFilling}
                        inverted={inverted}
                    />
                )}

                {fields?.fencingDestination && fields?.fencingDestination?.map((fencingDesti) => (
                    <FormFieldRecapItemOption
                        key={fencingDesti}
                        fieldKey={IFormFieldKey.FencingDestination}
                        option={fencingDesti}
                        inverted={inverted}
                    />
                ))}

                {fields?.windowBlindsDisposition && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WindowBlindsDisposition}
                        fieldShape={fields?.windowBlindsDisposition}
                        fieldShapeTumbnail={fields?.windowBlindsDisposition}
                        inverted={inverted}
                    />
                )}

                {fields?.shapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.ShapeDimension}
                        fieldShape={fields?.shapeDimension?.shape}
                        fieldShapeTumbnail={fields?.shapeDimension?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsDwelling && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WindowBlindsDwelling}
                        fieldShape={fields?.windowBlindsDwelling}
                        fieldShapeTumbnail={fields?.windowBlindsDwelling}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsStyle && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WindowBlindsStyle}
                        fieldShape={fields?.windowBlindsStyle}
                        fieldShapeTumbnail={fields?.windowBlindsStyle}
                        inverted={inverted}
                    />
                )}

                {fields?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.Shape}
                        fieldShape={fields?.shape}
                        fieldShapeTumbnail={fields?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsDimension && Object
                    .entries(fields?.windowBlindsDimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.windowBlindsProtection && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowBlindsProtection}
                        option={fields?.windowBlindsProtection}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsManeuver && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowBlindsManeuver}
                        option={fields?.windowBlindsManeuver}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsFilling && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowBlindsFilling}
                        option={fields?.windowBlindsFilling}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsFunction && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WindowBlindsFunction}
                        fieldShape={fields?.windowBlindsFunction}
                        fieldShapeTumbnail={fields?.windowBlindsFunction}
                        inverted={inverted}
                    />
                )}

                {fields?.windowBlindsGlassDimension && Object
                    .entries(fields?.windowBlindsGlassDimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.frameShapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.FrameShapeDimension}
                        fieldShape={fields?.frameShapeDimension?.shape}
                        fieldShapeTumbnail={fields?.frameShapeDimension?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.frameShapeDimension?.dimension && Object
                    .entries(fields?.frameShapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.claustraDestination && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.ClaustraDestination}
                        fieldShape={fields?.claustraDestination}
                        fieldShapeTumbnail={fields?.claustraDestination}
                        inverted={inverted}
                    />
                )}

                {fields?.destinationShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.DestinationShape}
                        fieldShape={fields?.destinationShape}
                        fieldShapeTumbnail={fields?.destinationShape}
                        inverted={inverted}
                    />
                )}

                {fields?.claustraMobility && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.ClaustraMobility}
                        fieldShape={fields?.claustraMobility}
                        fieldShapeTumbnail={fields?.claustraMobility}
                        inverted={inverted}
                    />
                )}

                {fields?.claustraType && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.ClaustraType}
                        fieldShape={fields?.claustraType}
                        fieldShapeTumbnail={fields?.claustraType}
                        inverted={inverted}
                    />
                )}

                {fields?.wineCellarRoom && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WineCellarRoom}
                        fieldShape={fields?.wineCellarRoom}
                        fieldShapeTumbnail={fields?.wineCellarRoom}
                        inverted={inverted}
                    />
                )}

                {fields?.wineCellarShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WineCellarShape}
                        fieldShape={fields?.wineCellarShape}
                        fieldShapeTumbnail={fields?.wineCellarShape}
                        inverted={inverted}
                    />
                )}

                {fields?.wineCellarMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.wineCellarMaterial}
                        fieldKey={IFormFieldKey.WineCellarMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.wineCellarSquareMeter && fields?.wineCellarSquareMeter.squareMeter !== null && (
                    Object.entries(fields?.wineCellarSquareMeter)
                        .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                        .map(([dimensionKey, dimensionField]) => (
                            <FormFieldRecapItemDimension
                                key={dimensionKey}
                                dimensionKey={dimensionKey}
                                dimensionField={dimensionField}
                                inverted={inverted}
                            />
                        )))}

                {fields?.wineCellarHumidity?.humidity !== undefined
                    && fields?.wineCellarHumidity.humidity !== null && (
                    Object.entries(fields?.wineCellarHumidity)
                        .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                        .map(([dimensionKey, dimensionField]) => (
                            <FormFieldRecapItemDimension
                                key={dimensionKey}
                                dimensionKey={dimensionKey}
                                dimensionField={dimensionField}
                                inverted={inverted}
                            />
                        )))}

                {fields?.wineCellarDegrees?.degrees !== undefined
                    && fields?.wineCellarDegrees.degrees !== null && (
                    Object.entries(fields?.wineCellarDegrees)
                        .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                        .map(([dimensionKey, dimensionField]) => (
                            <FormFieldRecapItemDimension
                                key={dimensionKey}
                                dimensionKey={dimensionKey}
                                dimensionField={dimensionField}
                                inverted={inverted}
                            />
                        )))}

                {fields?.wineCellarPose && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WineCellarPose}
                        fieldShape={fields?.wineCellarPose}
                        fieldShapeTumbnail={fields?.wineCellarPose}
                        inverted={inverted}
                    />
                )}

                {fields?.wineCellarDoor && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WineCellarDoor}
                        fieldShape={fields?.wineCellarDoor}
                        fieldShapeTumbnail={fields?.wineCellarDoor}
                        inverted={inverted}
                    />
                )}

                {wineCellarNbDoor && wineCellarNbDoor.length > 0 && (
                    <>
                        {wineCellarNbDoor.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </>
                )}

                {wineCellarSensDoor && (
                    <>
                        {wineCellarSensDoor.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </>
                )}

                {fields?.staircaseSideTurn && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.StaircaseSideTurn}
                        fieldShape={fields?.staircaseSideTurn}
                        fieldShapeTumbnail={fields?.staircaseSideTurn}
                        inverted={inverted}
                    />
                )}

                {fields?.staircaseLanding && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.StaircaseLanding}
                        fieldShape={fields?.staircaseLanding}
                        fieldShapeTumbnail={fields?.staircaseLanding}
                        inverted={inverted}
                    />
                )}

                {fields?.limonShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.LimonShape}
                        fieldShape={fields?.limonShape}
                        fieldShapeTumbnail={fields?.limonShape}
                        inverted={inverted}
                    />
                )}

                {fields?.pergolaShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.PergolaShape}
                        fieldShape={fields?.pergolaShape}
                        fieldShapeTumbnail={fields?.pergolaShape}
                        inverted={inverted}
                    />
                )}

                {fields?.highWindDestination && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.HighWindDestination}
                        fieldShape={fields?.highWindDestination}
                        fieldShapeTumbnail={fields?.highWindDestination}
                        inverted={inverted}
                    />
                )}

                {fields?.plainSteps && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.PlainSteps}
                        fieldShape={fields?.plainSteps}
                        fieldShapeTumbnail={fields?.plainSteps}
                        inverted={inverted}
                    />
                )}

                {fields?.mirrorFrameMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.mirrorFrameMaterial}
                        fieldKey={IFormFieldKey.MirrorFrameMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.material && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.material}
                        fieldKey={IFormFieldKey.Material}
                        inverted={inverted}
                    />
                )}

                {fields?.closetDoorFrameMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.closetDoorFrameMaterial}
                        fieldKey={IFormFieldKey.ClosetDoorFrameMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.roofShapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.RoofShapeDimension}
                        fieldShape={fields?.roofShapeDimension?.shape}
                        fieldShapeTumbnail={fields?.roofShapeDimension?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.roofShapeDimension?.dimension && Object
                    .entries(fields?.roofShapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.windowMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.windowMaterial}
                        fieldKey={IFormFieldKey.WindowMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.frameMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.frameMaterial}
                        fieldKey={IFormFieldKey.FrameMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.secondMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.secondMaterial}
                        fieldKey={IFormFieldKey.SecondMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.glassWallStyle && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.GlassWallStyle}
                        fieldShape={fields?.glassWallStyle}
                        fieldShapeTumbnail={fields?.glassWallStyle}
                        inverted={inverted}
                    />
                )}

                {fields?.closingType && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.ClosingType}
                        fieldShape={fields?.closingType}
                        fieldShapeTumbnail={fields?.closingType}
                        inverted={inverted}
                    />
                )}

                {fields?.fenceDestination && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.FenceDestination}
                        fieldShape={fields?.fenceDestination}
                        fieldShapeTumbnail={fields?.fenceDestination}
                        inverted={inverted}
                    />
                )}

                {fields?.fenceFilling && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.FenceFilling}
                        fieldShape={fields?.fenceFilling}
                        fieldShapeTumbnail={fields?.fenceFilling}
                        inverted={inverted}
                    />
                )}

                {fields?.fenceMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.fenceMaterial}
                        fieldKey={IFormFieldKey.FenceMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.fenceSecondMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.fenceSecondMaterial}
                        fieldKey={IFormFieldKey.FenceSecondMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.dimension && Object
                    .entries(fields?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.stepsDimension && Object
                    .entries(fields?.stepsDimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.border && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.Border}
                        fieldShape={fields?.border}
                        fieldShapeTumbnail={fields?.border}
                        inverted={inverted}
                    />
                )}

                {fields.shapeDimension?.dimension && Object
                    .entries(fields.shapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.type && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.Type}
                        fieldShape={fields?.type}
                        fieldShapeTumbnail={fields?.type}
                        inverted={inverted}
                    />
                )}

                {fields?.closetDoorSens && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.ClosetDoorSens}
                        option={fields?.closetDoorSens}
                        inverted={inverted}
                    />
                )}

                {fields?.kitchenStyle && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.KitchenStyle}
                        fieldShape={fields?.kitchenStyle}
                        fieldShapeTumbnail={fields?.kitchenStyle}
                        inverted={inverted}
                    />
                )}

                {fields?.kitchenShape?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.KitchenShape}
                        fieldShape={fields?.kitchenShape?.shape}
                        fieldShapeTumbnail={fields?.kitchenShape?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.kitchenShape?.dimension && Object
                    .entries(fields?.kitchenShape?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

                {fields?.kitchenLayout && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.KitchenLayout}
                        fieldShape={fields?.kitchenLayout}
                        fieldShapeTumbnail={fields?.kitchenLayout}
                        inverted={inverted}
                    />
                )}

                {fields?.kitchenMaterialShade && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.KitchenMaterialShade}
                        fieldShape={fields?.kitchenMaterialShade}
                        fieldShapeTumbnail={fields?.kitchenMaterialShade}
                        inverted={inverted}
                    />
                )}

                {fields?.kitchenHandle && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.KitchenHandle}
                        fieldShape={fields?.kitchenHandle}
                        fieldShapeTumbnail={fields?.kitchenHandle}
                        inverted={inverted}
                    />
                )}

                {fields?.garageDoorOpening && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.GarageDoorOpening}
                        fieldShape={fields?.garageDoorOpening}
                        fieldShapeTumbnail={fields?.garageDoorOpening}
                        inverted={inverted}
                    />
                )}

                {fields?.garageDoorGroove && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.GarageDoorGroove}
                        fieldShape={fields?.garageDoorGroove}
                        fieldShapeTumbnail={fields?.garageDoorGroove}
                        inverted={inverted}
                    />
                )}

                {fields?.pergolaRoof && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.PergolaRoof}
                        option={fields?.pergolaRoof}
                        inverted={inverted}
                    />
                )}

                {fields?.groundShapeDimension?.shape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.GroundShapeDimension}
                        fieldShape={fields?.groundShapeDimension?.shape}
                        fieldShapeTumbnail={fields?.groundShapeDimension?.shape}
                        inverted={inverted}
                    />
                )}

                {fields?.groundShapeDimension?.dimension && Object
                    .entries(fields?.groundShapeDimension?.dimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}

            </FormFieldRecapContainerWithTitle>

            {fields?.fenceRailing?.material !== undefined
                && fields?.fenceRailing?.material !== null && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.FenceRailing}.label`} />
                        </Typography>
                    )}
                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {fields?.fenceRailing && (
                            <FormFieldRecapItemMaterial
                                fieldMaterial={fields?.fenceRailing}
                                fieldKey={IFormFieldKey.FenceRailing}
                                inverted={inverted}
                            />
                        )}
                    </Grid>
                </>
            )}

            {fields?.staircaseRailing?.material !== undefined
                && fields?.staircaseRailing?.material !== null && (
                <FormFieldRecapContainerWithTitle
                    title={`fieldsChat.${IFormFieldKey.StaircaseRailing}.label`}
                    showTitle={shouldHideSubTitle}
                >
                    {fields?.staircaseRailing && (
                        <FormFieldRecapItemMaterial
                            fieldMaterial={fields?.staircaseRailing}
                            fieldKey={IFormFieldKey.StaircaseRailing}
                            inverted={inverted}
                        />
                    )}
                </FormFieldRecapContainerWithTitle>
            )}

            { fields?.staircaseGuard?.materialPrimary?.material !== undefined
                && fields?.staircaseGuard?.materialPrimary?.material !== null && (
                <FormFieldRecapContainerWithTitle
                    title={`fieldsChat.${IFormFieldKey.StaircaseGuard}.label`}
                    showTitle={shouldHideSubTitle}
                >
                    {fields?.staircaseGuard?.materialPrimary && (
                        <FormFieldRecapItemMaterial
                            fieldMaterial={fields?.staircaseGuard?.materialPrimary}
                            fieldKey={IFormFieldKey.StaircaseGuard}
                            inverted={inverted}
                        />
                    )}

                    {fields?.staircaseGuard?.materialSecondary && (
                        <FormFieldRecapItemMaterial
                            fieldMaterial={fields?.staircaseGuard?.materialSecondary}
                            fieldKey={IFormFieldKey.StaircaseGuard}
                            inverted={inverted}
                        />
                    )}
                </FormFieldRecapContainerWithTitle>
            )}

            {fields?.fencePoles?.material?.material !== undefined
                && fields?.fencePoles?.material.material !== null
                && fields?.fencePoles?.shape !== undefined
                && fields?.fencePoles?.shape !== null && (
                <FormFieldRecapContainerWithTitle
                    title={`fieldsChat.${IFormFieldKey.FencePoles}.label`}
                    showTitle={shouldHideSubTitle}
                >

                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.FencePoles}
                        fieldShape={fields?.fencePoles?.shape}
                        fieldShapeTumbnail={fields?.fencePoles?.shape}
                        inverted={inverted}
                    />

                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.fencePoles?.material}
                        fieldKey={IFormFieldKey.FencePoles}
                        inverted={inverted}
                    />
                </FormFieldRecapContainerWithTitle>
            )}

            {kitchenHomeAppliance !== undefined && kitchenHomeAppliance.some((element) => element !== undefined && element.value > 0)
                 && (
                     <>
                         {!shouldHideSubTitle && (
                             <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                                 <FormattedMessage id={`fieldsChat.${IFormFieldKey.KitchenHomeAppliance}.label`} />
                             </Typography>
                         )}

                         <Grid
                             container
                             spacing={1}
                             direction="row"
                             justify="flex-start"
                             alignItems="stretch"
                             style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                         >

                             {fields?.applianceLevel && (
                                 <FormFieldRecapItemContact
                                     label={intl.formatMessage({ id: `components.FormFieldRadioList.radioListOptions.applianceLevelOption.${fields?.applianceLevel}.label` })}
                                     value={intl.formatMessage({ id: `components.FormFieldRadioList.radioListOptions.applianceLevelOption.${fields?.applianceLevel}.recap` })}
                                     inverted={inverted}
                                 />
                             )}

                             {kitchenHomeAppliance?.map((quantifiedShape) => quantifiedShape.value > 0 && (
                                 <FormFieldRecapItemShapeQuantity
                                     key={quantifiedShape.id}
                                     fieldShape={quantifiedShape.id}
                                     quantity={quantifiedShape.value}
                                     fieldShapeTumbnail={quantifiedShape.id}
                                     inverted={inverted}
                                 />
                             ))}

                         </Grid>
                     </>
                 )}

            <FormFieldRecapContainerWithTitle
                title={`fieldsChat.${IFormFieldKey.AddOns}.label`}
                showTitle={shouldHideSubTitle}
            >
                {addOns && addOns.map((quantifiedShape) => quantifiedShape.value > 0 && (
                    <FormFieldRecapItemShapeQuantity
                        key={quantifiedShape.id}
                        fieldShape={quantifiedShape.id}
                        quantity={quantifiedShape.value}
                        fieldShapeTumbnail={quantifiedShape.id}
                        inverted={inverted}
                    />
                ))}

                {fields?.portalMotorization && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.PortalMotorization}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.portalLaying !== null && fields?.portalLaying !== undefined && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.PortalLaying}.label` })}`}
                                    value={`${intl.formatMessage({ id: fields?.portalLaying ? 'yes' : 'no' })}`}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.projectInformation?.laying !== null && fields?.projectInformation?.laying !== undefined && (
                    <FormFieldRecapItemContact
                        label={intl.formatMessage({ id: 'components.FormFieldProjectInformation.laying.label' })}
                        value={intl.formatMessage({ id: `${fields?.projectInformation.laying}` })}
                        inverted={inverted}
                    />
                )}

                {fields?.projectInformation?.accompaniment !== null && fields?.projectInformation?.accompaniment !== undefined && (
                    <FormFieldRecapItemContact
                        label={intl.formatMessage({ id: 'components.FormFieldProjectInformation.accompaniment.label' })}
                        value={intl.formatMessage({ id: `${fields?.projectInformation.accompaniment}` })}
                        inverted={inverted}
                    />
                )}

                {shoesQuantity && shoesQuantity.map((quantifiedShape) => quantifiedShape.value > 0 && (
                    <FormFieldRecapItemShapeQuantity
                        key={quantifiedShape.id}
                        fieldShape={quantifiedShape.id}
                        quantity={quantifiedShape.value}
                        fieldShapeTumbnail={quantifiedShape.id}
                        inverted={inverted}
                    />
                ))}

                {quantity && quantity.map((quantifiedShape) => quantifiedShape.value > 0 && (
                    <FormFieldRecapItemShapeQuantity
                        key={quantifiedShape.id}
                        fieldShape={quantifiedShape.id}
                        quantity={quantifiedShape.value}
                        fieldShapeTumbnail={quantifiedShape.id}
                        inverted={inverted}
                    />
                ))}

                {fields?.fenceProtection && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.FenceProtection}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.claustraLaying && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.ClaustraLaying}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.windowLeaf && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.WindowLeaf}
                        fieldShape={fields?.windowLeaf}
                        fieldShapeTumbnail={fields?.windowLeaf}
                        inverted={inverted}
                    />
                )}

                {fields?.windowOpening && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowOpening}
                        option={fields?.windowOpening}
                        inverted={inverted}
                    />
                )}

                {fields?.windowGlazing && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowGlazing}
                        option={fields?.windowGlazing}
                        inverted={inverted}
                    />
                )}

                {fields?.windowInsulation && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowInsulation}
                        option={fields?.windowInsulation}
                        inverted={inverted}
                    />
                )}

                {fields?.windowInstallation && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.WindowInstallation}.label` })}`}
                                    value={intl.formatMessage({ id: fields?.windowInstallation ? 'yes' : 'no' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.wineCellarLightingExpose && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.WineCellarLightingExpose}.label` })}`}
                                    value={intl.formatMessage({ id: fields?.wineCellarLightingExpose ? 'yes' : 'no' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.multiSelector && fields?.multiSelector.map((multiSelectorOption) => (
                    <FormFieldRecapItemOption
                        key={multiSelectorOption}
                        fieldKey={IFormFieldKey.MultiSelector}
                        option={multiSelectorOption}
                        inverted={inverted}
                    />
                ))}

                {fields?.background && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.Background}.label` })}`}
                                    value={intl.formatMessage({ id: fields?.background ? 'yes' : 'no' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.windowBlindsElectric && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.WindowBlindsElectric}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.windowBlindsLight && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.WindowBlindsLight}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.staircaseProtection && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.StaircaseProtection}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.staircaseLayingStairs !== undefined && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.StaircaseLayingStairs}.label` })}`}
                                    value={`${intl.formatMessage({ id: fields?.staircaseLayingStairs ? 'yes' : 'no' })}`}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.deckBoardShape && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.DeckBoardShape}.label` })}`}
                                    value={intl.formatMessage({ id: `shapes.${fields?.deckBoardShape}.name` })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.highWindLaying !== undefined && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.HighWindLaying}.label` })}`}
                                    value={`${intl.formatMessage({ id: fields?.highWindLaying ? 'yes' : 'no' })}`}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.deckBoardWidth && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.DeckBoardWidth}
                        option={fields?.deckBoardWidth}
                        inverted={inverted}
                    />
                )}

                {fields?.terraceLight && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.TerraceLight}.label` })}`}
                                    value={`${intl.formatMessage({ id: fields?.terraceLight ? 'yes' : 'no' })}`}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.windowBlindsPose !== undefined
                    && fields?.windowBlindsPose !== null && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.WindowBlindsPose}
                        option={fields?.windowBlindsPose}
                        inverted={inverted}
                    />
                )}

                {fields?.fenceGateDimension !== undefined
                    && fields?.fenceGateDimension?.height !== null && (
                    <>
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.FenceGateDimension}.label`} />
                        </Typography>
                        <Grid
                            container
                            spacing={1}
                            direction="row"
                            justify="flex-start"
                            alignItems="stretch"
                            style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                        >
                            {fields?.fenceGateDimension && Object
                                .entries(fields?.fenceGateDimension)
                                .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                                .map(([dimensionKey, dimensionField]) => (
                                    <FormFieldRecapItemDimension
                                        key={dimensionKey}
                                        dimensionKey={dimensionKey}
                                        dimensionField={dimensionField}
                                        inverted={inverted}
                                    />
                                ))}
                        </Grid>
                    </>
                )}

                {fields?.fencingPose !== undefined
                    && fields?.fencingPose !== null && (
                    <FormFieldRecapItemRadioListOption
                        fieldKey={IFormFieldKey.FencingPose}
                        option={fields?.fencingPose}
                        inverted={inverted}
                    />
                )}

                {fields?.terracePedestals && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.TerracePedestals}.label` })}`}
                                    value={`${intl.formatMessage({ id: fields?.terracePedestals ? 'yes' : 'no' })}`}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.terraceLaying && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.TerraceLaying}.label` })}`}
                                    value={`${intl.formatMessage({ id: fields?.terraceLaying ? 'yes' : 'no' })}`}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {fields?.garageDoorGate && (
                    <Grid item xs={6} style={recapGridItemStyle}>
                        <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                            <CardContent style={recapCardContentStyle}>
                                <FormFieldRecapItem
                                    label={`${intl.formatMessage({ id: `fieldsChat.${IFormFieldKey.GarageDoorGate}.label` })}`}
                                    value={intl.formatMessage({ id: 'yes' })}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                { fields?.garageDoorOptions !== undefined && (
                    <>
                        <Grid
                            container
                            spacing={1}
                            direction="row"
                            justify="flex-start"
                            alignItems="stretch"
                            style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                        >
                            {fields?.garageDoorOptions?.map((garageDoorOption) => (
                                <FormFieldRecapItemOption
                                    key={garageDoorOption}
                                    fieldKey={IFormFieldKey.GarageDoorOptions}
                                    option={garageDoorOption}
                                    inverted={inverted}
                                />
                            ))}
                        </Grid>
                    </>
                )}

            </FormFieldRecapContainerWithTitle>

            {fields?.portalGate !== undefined && fields?.portalGate?.height !== null && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.PortalGate}.label`} />
                        </Typography>
                    )}
                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {fields?.portalGate && Object
                            .entries(fields?.portalGate)
                            .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                            .map(([dimensionKey, dimensionField]) => (
                                <FormFieldRecapItemDimension
                                    key={dimensionKey}
                                    dimensionKey={dimensionKey}
                                    dimensionField={dimensionField}
                                    inverted={inverted}
                                />
                            ))}
                    </Grid>
                </>
            )}

            {(obstacles !== undefined && obstacles.some((element) => element !== undefined && element.value > 0)) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.Obstacles}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {obstacles.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {fields?.constraints !== undefined && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.Obstacles}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {fields?.constraints?.map((terraceConstraint) => (
                            <FormFieldRecapItemOption
                                key={terraceConstraint}
                                fieldKey={IFormFieldKey.Constraints}
                                option={terraceConstraint}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {kitchenFurniture && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.KitchenFurniture}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {kitchenFurniture.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {doors && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.Openings}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {doors.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {(wineCellarShelf || wineCellarNbBottle || fields?.wineCellarShelfMaterial) && (
                <>
                    {shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.WineCellarShelf}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {wineCellarShelf && wineCellarShelf.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}

                        {fields?.wineCellarShelfMaterial && (
                            <FormFieldRecapItemMaterial
                                fieldMaterial={fields?.wineCellarShelfMaterial}
                                fieldKey={IFormFieldKey.WineCellarShelfMaterial}
                                inverted={inverted}
                            />
                        )}

                        {wineCellarNbBottle && wineCellarNbBottle.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {(wineCellarOption !== undefined && wineCellarOption.some((element) => element !== undefined && element.value > 0)) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.AddOns}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {wineCellarOption.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {(pergolaOptions !== undefined && pergolaOptions.some((element) => element !== undefined && element.value > 0)) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.PergolaOptions}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {pergolaOptions.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {(connectics !== undefined && connectics.some((element) => element !== undefined && element.value > 0)) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.Connectics}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {connectics.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {(cutting !== undefined && cutting.some((element) => element !== undefined && element.value > 0)) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.Cutting}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {cutting.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            {(vegetals !== undefined && vegetals.some((element) => element !== undefined && element.value > 0)) && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.Vegetals}.label`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {vegetals.map((quantifiedShape) => quantifiedShape.value > 0 && (
                            <FormFieldRecapItemShapeQuantity
                                key={quantifiedShape.id}
                                fieldShape={quantifiedShape.id}
                                quantity={quantifiedShape.value}
                                fieldShapeTumbnail={quantifiedShape.id}
                                inverted={inverted}
                            />
                        ))}
                    </Grid>
                </>
            )}

            <FormFieldRecapContainerWithTitle
                title="forms.fieldsCategories.legs.title"
                showTitle={shouldHideSubTitle}
            >
                {fields?.legsMaterial && (
                    <FormFieldRecapItemMaterial
                        fieldMaterial={fields?.legsMaterial}
                        fieldKey={IFormFieldKey.LegsMaterial}
                        inverted={inverted}
                    />
                )}

                {fields?.legsShape && (
                    <FormFieldRecapItemShape
                        fieldKey={IFormFieldKey.LegsShape}
                        fieldShape={fields?.legsShape}
                        fieldShapeTumbnail={fields?.legsShape}
                        inverted={inverted}
                    />
                )}

                {fields?.legsDimension && Object
                    .entries(fields.legsDimension)
                    .filter((v): v is [FieldDimensionId, number] => v[1] !== undefined)
                    .map(([dimensionKey, dimensionField]) => (
                        <FormFieldRecapItemDimension
                            key={dimensionKey}
                            dimensionKey={dimensionKey}
                            dimensionField={dimensionField}
                            inverted={inverted}
                        />
                    ))}
            </FormFieldRecapContainerWithTitle>

            {fields.additionalData && (
                <>
                    {!shouldHideSubTitle && (
                        <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                            <FormattedMessage id={`fieldsChat.${IFormFieldKey.AdditionalData}.recap`} />
                        </Typography>
                    )}

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >
                        {fields.comment && (
                            <>
                                {Object
                                    .entries(fields.comment)
                                    .map(([commentKey, commentValue]) => commentValue && (
                                        <Grid item xs key={commentKey} style={recapGridItemStyle}>
                                            <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                                <CardContent style={recapCardContentStyle}>
                                                    <FormFieldRecapItem
                                                        label={intl.formatMessage({ id: `components.FormFieldAdditionalData.${commentKey}.label` })}
                                                        value={commentValue}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                            </>
                        )}

                        {Object
                            .entries(fields.additionalData)
                            .filter(([additionalDataKey]) => !(shouldHideLookingFor && additionalDataKey === IFormFieldAdditionalDataKey.LookingFor)
                            && ![IFormFieldAdditionalDataKey.IsPrivate].includes(additionalDataKey as IFormFieldAdditionalDataKey))
                            .map(([additionalDataKey, additionalDataValue]) => additionalDataValue && (
                                <Grid item xs key={additionalDataKey} style={recapGridItemStyle} {...(additionalDataKey === IFormFieldAdditionalDataKey.UploadFile && { xs: 12 })}>
                                    <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                        <CardContent style={recapCardContentStyle}>
                                            <FormFieldRecapItem
                                                label={intl.formatMessage({ id: `components.FormFieldAdditionalData.${additionalDataKey}.label` })}
                                                image={additionalDataKey === IFormFieldAdditionalDataKey.UploadFile ? uploadFileUrl : ''}
                                                value={
                                                    (additionalDataKey !== IFormFieldAdditionalDataKey.UploadFile && (
                                                        (additionalDataKey === IFormFieldAdditionalDataKey.Quantity && `${intl.formatMessage({ id: `components.FormFieldAdditionalData.${additionalDataKey}.value` }, { quantity: additionalDataValue })}`)
                                                            || (additionalDataKey === IFormFieldAdditionalDataKey.LookingFor && `${intl.formatMessage({ id: `components.FormFieldAdditionalData.${additionalDataKey}.${additionalDataValue}.title` })}`)
                                                            || (additionalDataKey === IFormFieldAdditionalDataKey.Emergency && `${intl.formatMessage({ id: `components.FormFieldAdditionalData.${additionalDataKey}.${additionalDataValue}.title` })}`)
                                                            || (additionalDataKey === IFormFieldAdditionalDataKey.Finishing && `${intl.formatMessage({ id: `components.FormFieldAdditionalData.${additionalDataKey}.${additionalDataValue}` })}`)
                                                            || (additionalDataKey === IFormFieldAdditionalDataKey.Budget && `${intl.formatMessage({ id: `components.FormFieldAdditionalData.${additionalDataKey}.value` }, { budget: additionalDataValue })}`)
                                                            || additionalDataValue
                                                    ))
                                                }
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}

                        {secondaryUploadFile && (
                            <Grid item xs={12} style={recapGridItemStyle} {...(fields.uploadFiles)}>
                                <Card elevation={0} style={{ ...recapCardStyle, ...(inverted && { backgroundColor: lightGreyColor }) }}>
                                    <CardContent style={recapCardContentStyle}>
                                        <FormFieldRecapItem
                                            label={intl.formatMessage({ id: 'components.UploadFiles.label' })}
                                            image={secondaryUploadFile ? uploadFileUrl : ''}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}

            {fields.contact && (
                <>
                    <Typography variant="body1" variantMapping={{ body1: 'h3' }} color="primary">
                        <FormattedMessage id={`fieldsChat.${IFormFieldKey.Contact}.label`} />
                    </Typography>

                    <Grid
                        container
                        spacing={1}
                        direction="row"
                        justify="flex-start"
                        alignItems="stretch"
                        alignContent="stretch"
                        style={!shouldHideSubTitle ? recapGridContainerStyle : recapGridContainerFormFlowStyle}
                    >

                        {!shouldHideContact && (
                            <>
                                <FormFieldRecapItemContact
                                    label={intl.formatMessage({ id: 'components.FormFieldContact.availabilityRecap' })}
                                    value={intl.formatMessage({ id: `components.FormFieldContact.availabilityOptions.${fields?.contact.availability}` })}
                                    inverted={inverted}
                                />

                                <FormFieldRecapItemContact
                                    label={intl.formatMessage({ id: 'components.FormFieldContact.personaRecap' })}
                                    value={intl.formatMessage({ id: `components.FormFieldContact.personaOptions.${fields?.contact.persona}` })}
                                    inverted={inverted}
                                />

                                <FormFieldRecapItemContact
                                    label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.FirstName}` })}
                                    value={fields?.contact.firstName}
                                    inverted={inverted}
                                />

                                <FormFieldRecapItemContact
                                    label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.LastName}` })}
                                    value={fields?.contact.lastName}
                                    inverted={inverted}
                                />

                                <FormFieldRecapItemContact
                                    label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.Phone}` })}
                                    link={`tel:${fields?.contact.phone}`}
                                    value={fields?.contact.phone}
                                    inverted={inverted}
                                />

                                <FormFieldRecapItemContact
                                    label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.Email}` })}
                                    link={`mailto:${fields?.contact.email}`}
                                    value={fields?.contact.email}
                                    inverted={inverted}
                                />
                            </>
                        )}
                        <>
                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.ShippingAddressZip}` })}
                                value={fields?.contact.shippingAddressZip}
                                inverted={inverted}
                            />

                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.ShippingAddressCountry}` })}
                                value={fields?.contact.shippingAddressCountry}
                                inverted={inverted}
                            />

                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.ShippingAddressAccess}` })}
                                value={intl.formatMessage({ id: `components.FormFieldContact.accessOptions.${fields?.contact.shippingAddressAccess}` })}
                                inverted={inverted}
                            />

                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: `components.FormFieldContact.${IFormFieldContactKey.ShippingAddressFloor}` })}
                                value={intl.formatMessage({ id: `components.FormFieldContact.floorOptions.${fields?.contact.shippingAddressFloor}` })}
                                inverted={inverted}
                            />

                            <FormFieldRecapItemContact
                                label={intl.formatMessage({ id: 'recapCreationDate' })}
                                value={creationDate ? `${formatDate(creationDate)} - ${intl.formatTime(creationDate)}` : '-'}
                                inverted={inverted}
                            />
                        </>
                    </Grid>
                </>
            )}
        </>
    ) : (
        <div>No forms found</div>
    );
};

export default FormFieldRecap;
